import {Alpha3CountryCode} from 'platform/components';

import {always, isNil, isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {
  BlockUserApiArg,
  BlockUserApiResponse,
  ChangeCurrentUserPasswordApiArg,
  ChangeCurrentUserPasswordApiResponse,
  CreateExternalUserApiArg,
  CreateExternalUserApiResponse,
  CreateUserAbsenceApiArg,
  CreateUserAbsenceApiArgSchema,
  CreateUserAbsenceApiResponse,
  CreateUserApiArg,
  CreateUserApiResponse,
  DeleteUserAbsenceApiArg,
  DeleteUserAbsenceApiArgSchema,
  DeleteUserAbsenceApiResponse,
  GetCurrentUserInfoApiResponse,
  GetDelegatedUserAbsencesApiResponse,
  GetOtherUserSettingsApiArg,
  GetOtherUserSettingsApiResponse,
  GetUserAbsencesApiArg,
  GetUserAbsencesApiArgSchema,
  GetUserAbsencesApiResponse,
  GetUserApiArg,
  GetUserApiResponse,
  GetUserAuctionStatsApiResponse,
  GetUserAvailabilitySummaryApiArg,
  GetUserAvailabilitySummaryApiResponse,
  GetUserDashboardSettingsApiArg,
  GetUserDashboardSettingsApiResponse,
  GetUserMenuPinsArg,
  GetUserMenuPinsResponse,
  GetUsersApiResponse,
  GetUsersQueryParams,
  GetUserSettingsApiResponse,
  GetUserStats,
  GetUserStatsApiResponse,
  InactivityPeriodType,
  MenuItemPin,
  PatchCurrentUserApiArg,
  PatchCurrentUserApiResponse,
  PatchUserApiArg,
  PatchUserApiResponse,
  SetUserMenuPinsArg,
  PutUserDashboardSettingsApiArg,
  PutUserDashboardSettingsApiResponse,
  SetOtherUserSettingsItemApiArg,
  SetOtherUserSettingsItemApiResponse,
  SetUserSettingsItemApiArg,
  SetUserSettingsItemApiResponse,
  UnblockUserApiArg,
  UnblockUserApiResponse,
  SetUserMenuPinsResponse,
  GetUserAvatarApiResponse,
  GetUserAvatarApiArg,
  GetUserAvatarListApiResponse,
  GetUserAvatarListApiArg,
  GetUserAvatarByUriApiResponse,
  GetUserAvatarByUriApiArg,
  TRANSLATED_LANGUAGES,
  COMMON_CURRENCY,
  PricingStrategy,
  UserSidebarIconSize,
  ActivateUserApiArg,
  ActivateUserApiResponse,
  DeactivateUserApiArg,
  DeactivateUserApiResponse,
  SetUserAwayApiArg,
  SetUserAwayApiResponse,
  ActivateUserApiResponseSchema,
  ActivateUserApiArgSchema,
  DeactivateUserApiResponseSchema,
  DeactivateUserApiArgSchema,
  SetUserAwayApiResponseSchema,
  SetUserAwayApiArgSchema,
  ReActivateUserApiArg,
  ReActivateUserApiResponse,
  ReActivateUserRequestBodySchema,
  ReActivateUserResponseSchema,
  ReinviteUserApiArg,
  ReinviteUserApiResponse,
  GetUserInvitationApiArg,
  GetUserInvitationApiResponse,
  UserInvitationResponseBodySchema,
  InitializeUserApiArg,
  InitializeUserApiResponse,
  UpdateUserAbsenceApiArg,
  UpdateUserAbsenceApiArgSchema,
  UpdateUserAbsenceApiResponse,
  UpdateUserAbsenceApiResponseSchema,
  DeleteUserAbsenceApiResponseSchema,
  UserAbsenceResponseBodySchema,
  UserAvailabilitySummaryResponseBodySchema,
  PutUserDashboardArrangementApiArg,
  PutUserDashboardArrangementApiResponse,
  UserDashboardSettingsResponseBodySchema,
  UuidResponseBodySchema,
} from '@dms/api/shared';
import {defaultMenuPins} from '@dms/config';

import {getSetUserSettingsInvalidationTags} from './utils/getSetUserSettingsInvalidationTags';

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

export const userApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentUserInfo: build.query<GetCurrentUserInfoApiResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const [userInfoResponse, userSettingsResponse] = await Promise.all([
          fetchWithBaseQuery({url: `/dms/v1/user/info`}),
          fetchWithBaseQuery({url: `/dms/v1/user/settings`}),
        ]);

        if (userInfoResponse.error) {
          throw userInfoResponse.error;
        }

        if (userSettingsResponse.error) {
          throw userSettingsResponse.error;
        }

        const userInfo = Object.assign(
          {isAway: false, awayChangedAt: null},
          userInfoResponse.data as Omit<GetCurrentUserInfoApiResponse, 'settings'>
        );
        const userSettings = userSettingsResponse.data as GetUserSettingsApiResponse;

        const data: GetCurrentUserInfoApiResponse = Object.assign({}, userInfo, {
          settings: {
            currency: (userSettings?.sourcingCurrency?.value ??
              COMMON_CURRENCY.czk) as COMMON_CURRENCY,
            insurerNumber: userSettings?.insurerNumber?.value as string | undefined,
            customId: userSettings?.customId?.value as string | undefined,
            branch: (userSettings?.activeBranch?.value ?? '') as string,
            country: (userSettings?.sourcingBuyerCountry?.value ?? '') as Alpha3CountryCode,
            pricingStrategy:
              userSettings?.sourcingPricingStrategy?.value === PricingStrategy.LFPP
                ? PricingStrategy.LFPP
                : PricingStrategy.FAIR_PRICE,
            language: (userSettings?.selectedLanguage?.value ??
              TRANSLATED_LANGUAGES.english) as TRANSLATED_LANGUAGES,
            inactivityPeriod: (userSettings?.inactivityPeriod?.value ?? '') as InactivityPeriodType,
            sidebarIconSize: (userSettings?.sidebarIconSize?.value ??
              'default') as UserSidebarIconSize,
          },
        });

        return {data};
      },
      providesTags: ['CurrentUser'],
    }),
    getCurrentUserSettings: build.query<GetUserSettingsApiResponse, void>({
      query: () => ({url: `/dms/v1/user/settings`}),
      providesTags: ['CurrentUser'],
    }),
    getUserStats: build.query<GetUserStats, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const [userAuctionStatsResponse, userStatsResponse] = await Promise.all([
          fetchWithBaseQuery({url: `/dms/v1/user/user-auction-stats`}),
          fetchWithBaseQuery({url: `/dms/v1/user/user-stats`}),
        ]);

        if (userAuctionStatsResponse.error) {
          throw userAuctionStatsResponse.error;
        }
        if (userStatsResponse.error) {
          throw userStatsResponse.error;
        }

        const userAuctionStats = userAuctionStatsResponse.data as GetUserAuctionStatsApiResponse;
        const userStats = userStatsResponse.data as GetUserStatsApiResponse;

        const data = Object.assign({}, {userAuctionStats, userStats});

        return {data};
      },
      providesTags: ['User'],
    }),
    createExternalUser: build.mutation<CreateExternalUserApiResponse, CreateExternalUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/external`,
        method: 'POST',
        body: queryArg.createExternalUserRequestBody,
      }),
    }),
    setUserSettingsItem: build.mutation<SetUserSettingsItemApiResponse, SetUserSettingsItemApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/settings/${queryArg.settingKey}`,
        method: 'PUT',
        body: queryArg.userSettingRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: SetUserSettingsItemApiArg) =>
        getSetUserSettingsInvalidationTags(queryArg.settingKey)
      ),
    }),
    blockUser: build.mutation<BlockUserApiResponse, BlockUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/block`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    changeCurrentUserPassword: build.mutation<
      ChangeCurrentUserPasswordApiResponse,
      ChangeCurrentUserPasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/me/password`,
        method: 'PATCH',
        body: queryArg.changeCurrentUserPasswordRequestBody,
      }),
    }),
    createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user`,
        method: 'POST',
        body: queryArg.createUserRequestBody,
      }),
    }),
    getUsers: build.query<GetUsersApiResponse, GetUsersQueryParams | void>({
      query: (queryArg) => ({
        url: `/dms/v1/user`,
        params: isNotNil(queryArg?.stateFilter) ? {stateFilter: queryArg?.stateFilter} : undefined,
      }),
      providesTags: ['Users'],
    }),
    getUser: build.query<GetUserApiResponse, GetUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.id}`,
      }),
      providesTags: (result, error, {id}) => [{type: 'User', id}],
    }),
    patchCurrentUser: build.mutation<PatchCurrentUserApiResponse, PatchCurrentUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/me`,
        method: 'PATCH',
        body: queryArg.updateCurrentUserRequestBody,
      }),
      invalidatesTags: ['CurrentUser'],
    }),
    patchUser: build.mutation<PatchUserApiResponse, PatchUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}`,
        method: 'PATCH',
        body: queryArg.updateUserRequestBody,
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'CurrentUser'],
    }),
    initializeUser: build.mutation<InitializeUserApiResponse, InitializeUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/initialize`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    getOtherUserSettings: build.query<GetOtherUserSettingsApiResponse, GetOtherUserSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/settings`,
      }),
      providesTags: (result, error, {userId}) => [{type: 'OtherUserSettings', id: userId}],
    }),
    setOtherUserSettingsItem: build.mutation<
      SetOtherUserSettingsItemApiResponse,
      SetOtherUserSettingsItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/settings/${queryArg.settingKey}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, {userId}) => [
        'CurrentUser',
        {type: 'OtherUserSettings', id: userId},
      ],
    }),
    unblockUser: build.mutation<UnblockUserApiResponse, UnblockUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/unblock`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    getUserMenuPins: build.query<GetUserMenuPinsResponse, GetUserMenuPinsArg>({
      query: () => ({
        url: `/dms/v1/web-app-user/pins`,
      }),
      providesTags: ['UserMenuPins', 'User'],
      transformResponse: (response: MenuItemPin[] | null | undefined) =>
        response ? response.map((i) => i?.item).filter(isNotNil) : [],
      async onQueryStarted(_arg, {dispatch, queryFulfilled}) {
        const result = await queryFulfilled.catch(always(null));
        // A 204 No Content is the BE sentinel for an uninitialized user. This is kept
        // out of the cached data (which stays `string[]`) and read from the HTTP status
        // so consumers never deal with a null. A failed query, or any 200 response
        // (including a deliberate empty `[]`), is left untouched.
        if (isNil(result) || result.meta?.response?.status !== 204) {
          return;
        }
        // Backfill the default pins once, in defaultMenuPins order. setUserMenuPins
        // optimistically patches this same cache entry to the full list, so the
        // sidebar renders immediately; the PUT then persists and rolls back on failure.
        dispatch(
          userApi.endpoints.setUserMenuPins.initiate({
            items: defaultMenuPins.map((item) => ({item})),
          })
        );
      },
    }),
    setUserMenuPins: build.mutation<SetUserMenuPinsResponse, SetUserMenuPinsArg>({
      query: (queryArg) => ({
        url: `/dms/v1/web-app-user/pins`,
        method: 'PUT',
        body: queryArg,
      }),
      async onQueryStarted({items: pins}, {dispatch, queryFulfilled}) {
        const newOrderedPins = pins.map(({item: pinId}) => pinId).filter(isNotNil);

        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserMenuPins', undefined, always(newOrderedPins))
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
    activateUser: build.mutation<ActivateUserApiResponse, ActivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/activate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: ActivateUserApiArgSchema,
        responseSchema: ActivateUserApiResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    deactivateUser: build.mutation<DeactivateUserApiResponse, DeactivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/deactivate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: DeactivateUserApiArgSchema,
        responseSchema: DeactivateUserApiResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    setUserAway: build.mutation<SetUserAwayApiResponse, SetUserAwayApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/away`,
        method: 'PUT',
        body: {
          isAway: queryArg.isAway,
        },
      }),
      extraOptions: {
        requestSchema: SetUserAwayApiArgSchema,
        responseSchema: SetUserAwayApiResponseSchema,
      },
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'User', id: queryArg.userId},
        'CurrentUser',
        'UserAvailabilitySummary',
      ],
    }),
    getUserAbsences: build.query<GetUserAbsencesApiResponse, GetUserAbsencesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/absence`,
      }),
      extraOptions: {
        requestSchema: GetUserAbsencesApiArgSchema,
        responseSchema: UserAbsenceResponseBodySchema.array(),
      },
      providesTags: (_result, _error, queryArg) => [{type: 'UserAbsence', id: queryArg.userId}],
    }),
    getDelegatedUserAbsences: build.query<GetDelegatedUserAbsencesApiResponse, void>({
      query: () => ({
        url: '/dms/v1/user/me/delegated-absence',
      }),
      extraOptions: {
        responseSchema: UserAbsenceResponseBodySchema.array(),
      },
    }),
    createUserAbsence: build.mutation<CreateUserAbsenceApiResponse, CreateUserAbsenceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/absence`,
        method: 'POST',
        body: queryArg.userAbsenceRequestBody,
      }),
      extraOptions: {
        requestSchema: CreateUserAbsenceApiArgSchema,
        responseSchema: UuidResponseBodySchema,
      },
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'UserAbsence', id: queryArg.userId},
        'UserAvailabilitySummary',
      ],
    }),
    updateUserAbsence: build.mutation<UpdateUserAbsenceApiResponse, UpdateUserAbsenceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/absence/${queryArg.userAbsenceId}`,
        method: 'PUT',
        body: queryArg.userAbsenceRequestBody,
      }),
      extraOptions: {
        requestSchema: UpdateUserAbsenceApiArgSchema,
        responseSchema: UpdateUserAbsenceApiResponseSchema,
      },
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'UserAbsence', id: queryArg.userId},
        'UserAvailabilitySummary',
      ],
    }),
    deleteUserAbsence: build.mutation<DeleteUserAbsenceApiResponse, DeleteUserAbsenceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/absence/${queryArg.userAbsenceId}`,
        method: 'DELETE',
      }),
      extraOptions: {
        requestSchema: DeleteUserAbsenceApiArgSchema,
        responseSchema: DeleteUserAbsenceApiResponseSchema,
      },
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'UserAbsence', id: queryArg.userId},
        'UserAvailabilitySummary',
      ],
    }),
    getUserAvailabilitySummary: build.query<
      GetUserAvailabilitySummaryApiResponse,
      GetUserAvailabilitySummaryApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user-availability-summary`,
        method: 'POST',
        body: {userIds: queryArg.userIds},
      }),
      extraOptions: {
        responseSchema: UserAvailabilitySummaryResponseBodySchema.array(),
      },
      providesTags: ['UserAvailabilitySummary'],
    }),
    reActivateUser: build.mutation<ReActivateUserApiResponse, ReActivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/re-activate`,
        method: 'POST',
        body: queryArg.reActivateUserRequestBody,
      }),
      extraOptions: {
        requestSchema: ReActivateUserRequestBodySchema,
        responseSchema: ReActivateUserResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    getUserDashboardSettings: build.query<
      GetUserDashboardSettingsApiResponse,
      GetUserDashboardSettingsApiArg
    >({
      query: () => ({
        url: `/dms/v1/user/dashboard/settings`,
      }),
      extraOptions: {
        responseSchema: UserDashboardSettingsResponseBodySchema,
      },
      providesTags: ['DashboardSettings'],
    }),
    putUserDashboardArrangement: build.mutation<
      PutUserDashboardArrangementApiResponse,
      PutUserDashboardArrangementApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/dashboard/arrangement`,
        method: 'PUT',
        body: queryArg,
      }),
      // Answers 204 and stores the tiles verbatim, so the patch is the whole update — no invalidation.
      onQueryStarted: async (queryArg, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserDashboardSettings', undefined, (draft) => {
            draft.arrangement = queryArg;
          })
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
    putUserDashboardSettings: build.mutation<
      PutUserDashboardSettingsApiResponse,
      PutUserDashboardSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/dashboard/settings`,
        method: 'PUT',
        body: queryArg,
      }),
      invalidatesTags: ['DashboardSettings'],
    }),
    getUserAvatarUrl: build.query<GetUserAvatarApiResponse, GetUserAvatarApiArg>({
      query: ({userId, height, width}) => ({
        url: `/dms/v1/user/${userId}/avatar`,
        params: {height, width},
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
      async onCacheEntryAdded(_arg, {cacheDataLoaded, cacheEntryRemoved}) {
        // Revoke the object URL once the cache entry is evicted, otherwise the
        // underlying Blob would stay allocated for the lifetime of the document.
        const objectUrl = await cacheDataLoaded.then(({data}) => data).catch(always(null));
        await cacheEntryRemoved;

        if (isNotNil(objectUrl)) {
          URL.revokeObjectURL(objectUrl);
        }
      },
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      providesTags: ['EmployeePhoto'],
    }),
    getUserAvatarList: build.query<GetUserAvatarListApiResponse, GetUserAvatarListApiArg>({
      // The API expects repeated `userIds[]` params, but RTK Query serializes array params
      // as a single comma-joined value, so the user ids are appended to the url manually.
      query: ({userIds, height}) => ({
        url: `/dms/v1/user/avatars?${new URLSearchParams(userIds.map((userId) => ['userIds[]', userId]))}`,
        params: {height},
      }),
      // The caller derives `userIds` from message order, so the same set of users can arrive in a
      // different order and would otherwise allocate a separate cache entry per permutation. The
      // response is keyed by `userId`, so order carries no meaning and is normalised away here.
      serializeQueryArgs: ({endpointName, queryArgs}) =>
        `${endpointName}(${JSON.stringify({
          userIds: [...queryArgs.userIds].sort(),
          height: queryArgs.height,
        })})`,
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      providesTags: ['EmployeePhoto'],
    }),
    getUserAvatarByUri: build.query<GetUserAvatarByUriApiResponse, GetUserAvatarByUriApiArg>({
      query: ({avatarUri}) => ({
        url: avatarUri,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
      async onCacheEntryAdded(_arg, {cacheDataLoaded, cacheEntryRemoved}) {
        // Revoke the object URL once the cache entry is evicted, otherwise the
        // underlying Blob would stay allocated for the lifetime of the document.
        const objectUrl = await cacheDataLoaded.then(({data}) => data).catch(always(null));
        await cacheEntryRemoved;

        if (isNotNil(objectUrl)) {
          URL.revokeObjectURL(objectUrl);
        }
      },
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      providesTags: ['EmployeePhoto'],
    }),
    reinviteUser: build.mutation<ReinviteUserApiResponse, ReinviteUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/invitation/refresh`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    getUserInvitation: build.query<GetUserInvitationApiResponse, GetUserInvitationApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/invitation`,
      }),
      extraOptions: {
        responseSchema: UserInvitationResponseBodySchema,
      },
      providesTags: (result, error, {userId}) => [{type: 'User', id: userId}],
    }),
  }),
});

export const selectCurrentUserInfo = userApi.endpoints.getCurrentUserInfo.select();

export const {
  useReinviteUserMutation,
  useLazyGetUserInvitationQuery,
  useGetUserAvatarUrlQuery,
  useLazyGetUserAvatarUrlQuery,
  useGetUserAvatarListQuery,
  useGetUserAvatarByUriQuery,
  useGetCurrentUserInfoQuery,
  useLazyGetCurrentUserInfoQuery,
  useGetCurrentUserSettingsQuery,
  useGetUserStatsQuery,
  useCreateExternalUserMutation,
  useSetUserSettingsItemMutation,
  useBlockUserMutation,
  useChangeCurrentUserPasswordMutation,
  useCreateUserMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  usePatchCurrentUserMutation,
  usePatchUserMutation,
  useUnblockUserMutation,
  useGetUserMenuPinsQuery,
  useSetUserMenuPinsMutation,
  useGetOtherUserSettingsQuery,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useSetUserAwayMutation,
  useGetUserAbsencesQuery,
  useLazyGetUserAbsencesQuery,
  useGetDelegatedUserAbsencesQuery,
  useCreateUserAbsenceMutation,
  useUpdateUserAbsenceMutation,
  useDeleteUserAbsenceMutation,
  useGetUserAvailabilitySummaryQuery,
  useSetOtherUserSettingsItemMutation,
  useGetUserDashboardSettingsQuery,
  useReActivateUserMutation,
  usePutUserDashboardSettingsMutation,
  usePutUserDashboardArrangementMutation,
  useInitializeUserMutation,
} = userApi;
